import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormHelperText,
  Grid,
  Autocomplete,
  MenuItem,
  InputAdornment,
  Box,
  Typography,
  Fade,
  Grow,
  Zoom,
  useTheme,
  styled,
  alpha,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Stack,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Numbers as NumbersIcon,
  TextFields as TextIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioIcon,
  ArrowDropDown as SelectIcon,
  Public as CountryIcon,
  Payment as PaymentIcon,
  Code as HtmlIcon,
  CalendarToday as DateIcon,
  LocationOn as LocationIcon,
  Business as CompanyIcon,
  Badge as BadgeIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  ContactPhone as ContactIcon,
  AccountBox as AccountIcon,
  Category as CategoryIcon,
  Title as TitleIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  VpnKey as KeyIcon,
  Link as LinkIcon,
  Web as WebIcon,
  Cake as CakeIcon,
  Wc as WcIcon,
  Face as FaceIcon,
  RecordVoiceOver as VoiceIcon,
  Message as MessageIcon,
  Subject as SubjectIcon,
  Note as NoteIcon,
  Comment as CommentIcon,
  Forum as ForumIcon,
  Help as HelpIcon,
  QuestionAnswer as QuestionIcon,
  Quiz as QuizIcon,
  Poll as PollIcon,
  Ballot as BallotIcon,
  Assignment as AssignmentIcon,
  LibraryBooks as LibraryIcon,
  MenuBook as BookIcon,
  Class as ClassIcon,
  Grade as GradeIcon,
  School as EducationIcon,
  Business as BusinessIcon,
  Work as JobIcon,
  MonetizationOn as MoneyIcon,
  AttachMoney as CurrencyIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon,
  LocalAtm as AtmIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as CartIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import countries from "../countries.json";
import { parseTheme } from "../utils/themeUtils";

// Custom hook for voice-to-text functionality
const useVoiceToText = (onResult) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();

    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = false;
    recognitionInstance.lang = "en-US";

    recognitionInstance.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (onResult) {
        onResult(transcript);
      }
    };

    recognitionInstance.onend = () => {
      setIsListening(false);
    };

    recognitionInstance.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    setRecognition(recognitionInstance);

    return () => {
      if (recognitionInstance) {
        recognitionInstance.stop();
      }
    };
  }, [onResult]);

  const startListening = () => {
    if (recognition && !isListening) {
      setIsListening(true);
      recognition.start();
    }
  };

  const stopListening = () => {
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
    }
  };

  return { isListening, startListening, stopListening };
};

// Voice Button Component
const VoiceButton = ({ onVoiceInput, disabled }) => {
  const { isListening, startListening, stopListening } =
    useVoiceToText(onVoiceInput);

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const isVoiceSupported =
    "webkitSpeechRecognition" in window || "SpeechRecognition" in window;

  if (!isVoiceSupported) {
    return null;
  }

  return (
    <Tooltip title={isListening ? "Stop recording" : "Start voice input"}>
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        size="small"
        sx={{
          color: isListening ? "error.main" : "text.secondary",
          "&:hover": {
            color: isListening ? "error.dark" : "primary.main",
          },
        }}
      >
        {isListening ? <MicIcon /> : <MicOffIcon />}
      </IconButton>
    </Tooltip>
  );
};

// Field type to icon mapping
const FIELD_ICONS = {
  text: TextIcon,
  email: EmailIcon,
  confirm_email: EmailIcon,
  phone: PhoneIcon,
  mobile: PhoneIcon,
  tel: PhoneIcon,
  number: NumbersIcon,
  checkbox: CheckBoxIcon,
  radio: RadioIcon,
  select: SelectIcon,
  country: CountryIcon,
  payment: PaymentIcon,
  html: HtmlIcon,
  date: DateIcon,
  mcq: BallotIcon,
  permission: SecurityIcon,
  // Field name based mapping
  name: PersonIcon,
  first_name: PersonIcon,
  "first name": PersonIcon,
  "First Name": PersonIcon,
  last_name: PersonIcon,
  "last name": PersonIcon,
  "Last Name": PersonIcon,
  full_name: PersonIcon,
  "full name": PersonIcon,
  "Full Name": PersonIcon,
  Designation: WorkIcon,
  designation: WorkIcon,
  organization: BusinessIcon,
  organization_name: BusinessIcon,
  "organization name": BusinessIcon,
  "Organization Name": BusinessIcon,
  organization_type: BusinessIcon,
  "organization type": BusinessIcon,
  "Organization Type": BusinessIcon,
  firstname: PersonIcon,
  lastname: PersonIcon,
  fullname: PersonIcon,
  title: TitleIcon,
  position: WorkIcon,
  job: JobIcon,
  company: CompanyIcon,
  organization: BusinessIcon,
  address: LocationIcon,
  city: LocationIcon,
  state: LocationIcon,
  country: CountryIcon,
  location: LocationIcon,
  website: WebIcon,
  url: LinkIcon,
  linkedin: LinkIcon,
  facebook: LinkIcon,
  twitter: LinkIcon,
  instagram: LinkIcon,
  whatsapp: PhoneIcon,
  age: CakeIcon,
  birthday: CakeIcon,
  birthdate: CakeIcon,
  dob: CakeIcon,
  gender: WcIcon,
  nationality: CountryIcon,
  passport: BadgeIcon,
  id: BadgeIcon,
  member_id: BadgeIcon,
  membership: BadgeIcon,
  registration_id: BadgeIcon,
  badge: BadgeIcon,
  department: CategoryIcon,
  division: CategoryIcon,
  team: CategoryIcon,
  group: CategoryIcon,
  category: CategoryIcon,
  type: CategoryIcon,
  specialization: SchoolIcon,
  qualification: EducationIcon,
  education: EducationIcon,
  degree: EducationIcon,
  university: SchoolIcon,
  college: SchoolIcon,
  school: SchoolIcon,
  institute: SchoolIcon,
  course: ClassIcon,
  subject: SubjectIcon,
  topic: SubjectIcon,
  interest: FaceIcon,
  hobby: FaceIcon,
  preference: SettingsIcon,
  bio: DescriptionIcon,
  description: DescriptionIcon,
  about: DescriptionIcon,
  notes: NoteIcon,
  comment: CommentIcon,
  comments: CommentIcon,
  message: MessageIcon,
  feedback: MessageIcon,
  question: QuestionIcon,
  answer: QuestionIcon,
  response: VoiceIcon,
  reply: ForumIcon,
  discussion: ForumIcon,
  help: HelpIcon,
  support: HelpIcon,
  assistance: HelpIcon,
  emergency: SecurityIcon,
  contact: ContactIcon,
  reference: InfoIcon,
  referral: InfoIcon,
  source: InfoIcon,
  how_did_you_hear: InfoIcon,
  dietary: InfoIcon,
  allergy: InfoIcon,
  medical: InfoIcon,
  health: InfoIcon,
  accommodation: HomeIcon,
  hotel: HomeIcon,
  room: HomeIcon,
  transportation: InfoIcon,
  flight: InfoIcon,
  travel: InfoIcon,
  visa: BadgeIcon,
  passport: BadgeIcon,
  document: AssignmentIcon,
  documents: AssignmentIcon,
  file: AssignmentIcon,
  files: AssignmentIcon,
  upload: AssignmentIcon,
  attachment: AssignmentIcon,
  attachments: AssignmentIcon,
  certificate: VerifiedIcon,
  certification: VerifiedIcon,
  license: VerifiedIcon,
  permit: VerifiedIcon,
  authorization: VerifiedIcon,
  approval: VerifiedIcon,
  verification: VerifiedIcon,
  validation: VerifiedIcon,
  authentication: SecurityIcon,
  login: SecurityIcon,
  password: KeyIcon,
  pin: KeyIcon,
  token: KeyIcon,
  key: KeyIcon,
  secret: SecurityIcon,
  private: SecurityIcon,
  perm: SecurityIcon,
  permissions: SecurityIcon,
  public: InfoIcon,
  general: InfoIcon,
  common: InfoIcon,
  standard: InfoIcon,
  basic: InfoIcon,
  advanced: InfoIcon,
  professional: WorkIcon,
  business: BusinessIcon,
  corporate: BusinessIcon,
  enterprise: BusinessIcon,
  commercial: BusinessIcon,
  industrial: BusinessIcon,
  technical: SettingsIcon,
  technology: SettingsIcon,
  innovation: SettingsIcon,
  research: SchoolIcon,
  development: SchoolIcon,
  design: SchoolIcon,
  creative: SchoolIcon,
  art: SchoolIcon,
  culture: SchoolIcon,
  social: PersonIcon,
  community: PersonIcon,
  public: PersonIcon,
  private: PersonIcon,
  personal: PersonIcon,
  individual: PersonIcon,
  family: PersonIcon,
  group: PersonIcon,
  team: PersonIcon,
  organization: BusinessIcon,
  company: CompanyIcon,
  institution: SchoolIcon,
  government: BadgeIcon,
  official: BadgeIcon,
  legal: BadgeIcon,
  financial: MoneyIcon,
  economic: MoneyIcon,
  budget: MoneyIcon,
  cost: MoneyIcon,
  price: MoneyIcon,
  amount: MoneyIcon,
  total: MoneyIcon,
  sum: MoneyIcon,
  payment: PaymentIcon,
  transaction: PaymentIcon,
  purchase: CartIcon,
  buy: CartIcon,
  sell: CartIcon,
  sale: CartIcon,
  order: CartIcon,
  invoice: ReceiptIcon,
  receipt: ReceiptIcon,
  bill: ReceiptIcon,
  account: AccountIcon,
  balance: BankIcon,
  bank: BankIcon,
  credit: CardIcon,
  debit: CardIcon,
  card: CardIcon,
  cash: AtmIcon,
  money: MoneyIcon,
  currency: CurrencyIcon,
  dollar: CurrencyIcon,
  euro: CurrencyIcon,
  pound: CurrencyIcon,
  yen: CurrencyIcon,
  rupee: CurrencyIcon,
  dirham: CurrencyIcon,
  riyal: CurrencyIcon,
  dinar: CurrencyIcon,
  franc: CurrencyIcon,
  peso: CurrencyIcon,
  real: CurrencyIcon,
  won: CurrencyIcon,
  yuan: CurrencyIcon,
  ruble: CurrencyIcon,
  rand: CurrencyIcon,
  krona: CurrencyIcon,
  krone: CurrencyIcon,
  zloty: CurrencyIcon,
  forint: CurrencyIcon,
  lira: CurrencyIcon,
  shekel: CurrencyIcon,
  baht: CurrencyIcon,
  ringgit: CurrencyIcon,
  singapore: CurrencyIcon,
  hong: CurrencyIcon,
  australian: CurrencyIcon,
  canadian: CurrencyIcon,
  new: CurrencyIcon,
  swiss: CurrencyIcon,
  norwegian: CurrencyIcon,
  swedish: CurrencyIcon,
  danish: CurrencyIcon,
  mexican: CurrencyIcon,
  brazilian: CurrencyIcon,
  indian: CurrencyIcon,
  chinese: CurrencyIcon,
  japanese: CurrencyIcon,
  korean: CurrencyIcon,
  thai: CurrencyIcon,
  malaysian: CurrencyIcon,
  indonesian: CurrencyIcon,
  philippine: CurrencyIcon,
  vietnamese: CurrencyIcon,
  taiwanese: CurrencyIcon,
  south: CurrencyIcon,
  russian: CurrencyIcon,
  turkish: CurrencyIcon,
  israeli: CurrencyIcon,
  saudi: CurrencyIcon,
  emirati: CurrencyIcon,
  kuwaiti: CurrencyIcon,
  qatari: CurrencyIcon,
  bahraini: CurrencyIcon,
  omani: CurrencyIcon,
  jordanian: CurrencyIcon,
  lebanese: CurrencyIcon,
  egyptian: CurrencyIcon,
  moroccan: CurrencyIcon,
  tunisian: CurrencyIcon,
  algerian: CurrencyIcon,
  libyan: CurrencyIcon,
  sudanese: CurrencyIcon,
  ethiopian: CurrencyIcon,
  kenyan: CurrencyIcon,
  ugandan: CurrencyIcon,
  tanzanian: CurrencyIcon,
  zambian: CurrencyIcon,
  zimbabwean: CurrencyIcon,
  botswanan: CurrencyIcon,
  namibian: CurrencyIcon,
  ghanaian: CurrencyIcon,
  nigerian: CurrencyIcon,
  ivorian: CurrencyIcon,
  senegalese: CurrencyIcon,
  malian: CurrencyIcon,
  burkinabe: CurrencyIcon,
  nigerien: CurrencyIcon,
  chadian: CurrencyIcon,
  cameroonian: CurrencyIcon,
  central: CurrencyIcon,
  congolese: CurrencyIcon,
  gabonese: CurrencyIcon,
  equatorial: CurrencyIcon,
  angolan: CurrencyIcon,
  mozambican: CurrencyIcon,
  madagascan: CurrencyIcon,
  mauritian: CurrencyIcon,
  seychellois: CurrencyIcon,
  comoran: CurrencyIcon,
  djiboutian: CurrencyIcon,
  somali: CurrencyIcon,
  eritrean: CurrencyIcon,
  rwandan: CurrencyIcon,
  burundian: CurrencyIcon,
  malawian: CurrencyIcon,
  swazi: CurrencyIcon,
  lesotho: CurrencyIcon,
  // Add more field name mappings as needed
};

// Styled components for better visual appeal
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiFormLabel-root": {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    transition: "all 0.3s ease",
    "&:hover": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& fieldset": {
        borderWidth: "2px",
        borderColor: theme.palette.primary.main,
      },
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  "&.Mui-checked": {
    color: theme.palette.primary.main,
  },
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  color: theme.palette.primary.main,
  "&.Mui-checked": {
    color: theme.palette.primary.main,
  },
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const AnimatedFormControl = ({ children, delay = 0 }) => (
  <Grow in={true} timeout={800} style={{ transformOrigin: "0 0 0" }}>
    <Box sx={{ animationDelay: `${delay}ms` }}>{children}</Box>
  </Grow>
);

const FormField = ({
  rhf,
  f,
  setValue,
  findCountryCodeByDialCode,
  selectedCountry,
  setSelectedCountry,
  selectedMobileCode,
  setSelectedMobileCode,
  parsedRegistration,
  pmt,
  setEmail,
  isEmailTaken,
  emailCheckLoading,
  isMobileTaken,
  mobileCheckLoading,
  showFamilyMembers,
  setOriginalPrice,
  setPrice,
  isMemberIdValid,
  memberDiscount,
  promoDiscount,
  promoFlat = 0,
  customTheme,
  featuresList = [], // Add featuresList prop
  prefilledEmail,
  socialProvider,
  uid, // Add UID prop for invitation links
  otherFieldState = { showOtherInput: false, otherValue: "" }, // State from parent
  updateOtherFieldState = () => {}, // Handler to update state in parent
}) => {
  const theme = useTheme();
  const errorMessage = rhf.fieldState?.error?.message;
  const parsedTheme = parseTheme(customTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Simple readonly logic for UID links: make text/email fields readonly
  const isUidFieldReadonly =
    uid &&
    (f.type === "text" ||
      f.type === "email" ||
      f.type === "number" ||
      f.type === "confirm_email" ||
      (f.type === undefined &&
        ![
          "checkbox",
          "radio",
          "payment",
          "select",
          "mcq",
          "country",
          "mobile_code",
          "calling_code",
          "permission",
          "html",
        ].includes(f.type)));

  // Sponsor pmt link → the company field is auto-filled from the sponsor's company + locked (owner
  // 2026-07-17). UX only; api-reg server-enforces the company on pmt links. Kept in sync with the
  // COMPANY_FIELD_VARIANTS list in RegistrationPage / api-reg's detectCompanyColumn.
  const COMPANY_FIELD_VARIANTS = [
    "company",
    "companyname",
    "company name",
    "company_name",
    "organization",
    "organization name",
    "organization_name",
    "organisation",
    "organisation name",
    "organisation_name",
  ];
  const isCompanyField = COMPANY_FIELD_VARIANTS.includes(
    String(f.name || "").trim().toLowerCase()
  );
  /*
   * Owner 2026-10-06: the registrant may edit their own company name.
   *
   * It had been locked since 2026-08-05 on the reasoning that the server enforces it anyway —
   * which it did, by overwriting whatever was typed with the sponsor's company. Both halves are
   * lifted together: unlocking the field alone would have let someone type a company that was
   * then silently discarded, which is worse than refusing the edit.
   *
   * The lock's styling and tooltip are kept and driven by this one flag, because the organiser
   * controls this per event and per sponsor from Reception next — at which point this constant
   * becomes that setting rather than a literal.
   */
  const COMPANY_LOCKED_BY_SPONSOR = false;
  const isPmtCompanyReadonly =
    COMPANY_LOCKED_BY_SPONSOR && !!pmt?.company && isCompanyField;

  // Debug logging for UID fields
  // if (uid) {
  //   console.log(
  //     `🔍 FormField Debug - Field: ${f.name}, Type: ${f.type}, Readonly: ${isUidFieldReadonly}`
  //   );
  // }
  // console.warn("isUidFieldReadonly", isUidFieldReadonly);
  // console.warn("uid", uid);
  // Check if features are enabled
  const isVoiceEnabled = featuresList.includes("voice-enable");
  const isIconEnabled = featuresList.includes("iconEnable");

  // Permission field state (always initialized, only used when f.type === "permission")
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customPermissions, setCustomPermissions] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState(() => {
    // Initialize from current field value if it's a permission field
    if (f?.type === "permission" && rhf.field.value) {
      return rhf.field.value
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);
    }
    return [];
  });

  // "Other" option state from parent component (persists across re-renders)
  const { showOtherInput, otherValue } = otherFieldState;

  // Local state for input value to ensure immediate updates (no async delay)
  const [localOtherValue, setLocalOtherValue] = useState(otherValue || "");
  const prevFieldNameRef = useRef(f?.name);

  // Reset local state only when switching to a different field (not on every render)
  useEffect(() => {
    if (prevFieldNameRef.current !== f?.name) {
      prevFieldNameRef.current = f?.name;
      setLocalOtherValue(otherValue || "");
    }
  }, [f?.name, otherValue]);

  // Sync with parent state only if local is empty (initial load, not while typing)
  useEffect(() => {
    if (!localOtherValue && otherValue) {
      setLocalOtherValue(otherValue);
    }
  }, [otherValue]);

  // Helper functions to update "Other" field state in parent
  const setShowOtherInput = (value) => {
    updateOtherFieldState({ showOtherInput: value });
  };
  const setOtherValue = (value) => {
    updateOtherFieldState({ otherValue: value });
  };

  // Check if "Other" is selected (for any field type)
  const isOtherSelected =
    f?.type === "select"
      ? rhf.field.value === "$Other" ||
        rhf.field.value === "Other" ||
        rhf.field.value === "other"
      : f?.type === "radio"
      ? rhf.field.value === "$Other" ||
        rhf.field.value === "Other" ||
        rhf.field.value === "other"
      : f?.type === "mcq"
      ? rhf.field.value?.includes("$Other") ||
        rhf.field.value?.includes("Other") ||
        rhf.field.value?.includes("other")
      : false;

  // Update showOtherInput state when selection changes
  useEffect(() => {
    if (f?.type === "select" || f?.type === "radio" || f?.type === "mcq") {
      // Check if form value is a custom value (doesn't match any option)
      const isCustomValue =
        f?.type === "select" || f?.type === "radio"
          ? rhf.field.value &&
            rhf.field.value !== "$Other" &&
            rhf.field.value !== "Other" &&
            rhf.field.value !== "other" &&
            !f.options?.includes(rhf.field.value)
          : f?.type === "mcq"
          ? Array.isArray(rhf.field.value) &&
            rhf.field.value.some((item) => item.startsWith("Other:"))
          : false;

      // Show "Other" input if:
      // 1. "Other" option is selected, OR
      // 2. Form value is a custom value (user has typed something), OR
      // 3. We have otherValue set (user is typing or has typed)
      const shouldShowOtherInput =
        isOtherSelected ||
        isCustomValue ||
        (otherValue && otherValue.trim().length > 0);

      // Update showOtherInput state
      // If it should be shown, set it to true (handlers also explicitly set it to true when typing)
      // Only hide it if "Other" is not selected AND no custom value AND no otherValue
      if (shouldShowOtherInput) {
        if (!showOtherInput) {
          setShowOtherInput(true);
        }
      } else if (
        !isOtherSelected &&
        !isCustomValue &&
        (!otherValue || !otherValue.trim())
      ) {
        // Only hide if truly not needed
        setShowOtherInput(false);
      }

      // If "Other" is selected or we have a custom value, extract it
      if (isOtherSelected && f?.type === "select") {
        const otherOptionValue =
          f.options?.find(
            (op) => op === "$Other" || op === "Other" || op === "other"
          ) || "Other";
        // If form value doesn't match the "Other" option, it's a custom value
        if (
          rhf.field.value &&
          rhf.field.value !== "$Other" &&
          rhf.field.value !== "Other" &&
          rhf.field.value !== "other" &&
          !f.options?.includes(rhf.field.value)
        ) {
          setOtherValue(rhf.field.value);
        }
      } else if (isOtherSelected && f?.type === "radio") {
        const otherOptionValue =
          f.options?.find(
            (op) => op === "$Other" || op === "Other" || op === "other"
          ) || "Other";
        // If form value doesn't match the "Other" option, it's a custom value
        if (
          rhf.field.value &&
          rhf.field.value !== "$Other" &&
          rhf.field.value !== "Other" &&
          rhf.field.value !== "other" &&
          !f.options?.includes(rhf.field.value)
        ) {
          setOtherValue(rhf.field.value);
        }
      } else if (isOtherSelected && f?.type === "mcq") {
        // For MCQ, check if array contains "Other: ..." custom values
        if (Array.isArray(rhf.field.value)) {
          const otherCustomValue = rhf.field.value.find((item) =>
            item.startsWith("Other:")
          );
          if (otherCustomValue) {
            // Extract the custom text after "Other: "
            const customText = otherCustomValue.replace(/^Other:\s*/, "");
            setOtherValue(customText);
          }
        }
      } else if (isCustomValue && f?.type === "select") {
        // If form value is custom, only update otherValue if it's currently empty (initial load)
        // This prevents the useEffect from overwriting user input while they're typing
        if (!otherValue || otherValue.length === 0) {
          setOtherValue(rhf.field.value);
        }
      } else if (isCustomValue && f?.type === "radio") {
        // If form value is custom, only update otherValue if it's currently empty (initial load)
        // This prevents the useEffect from overwriting user input while they're typing
        if (!otherValue || otherValue.length === 0) {
          setOtherValue(rhf.field.value);
        }
      } else if (!isOtherSelected && !isCustomValue) {
        // If "Other" is not selected and no custom value, clear the other value
        setOtherValue("");
      }
    }
  }, [isOtherSelected, f?.type, rhf.field.value, f?.options, otherValue]);

  // Get field icon based on type and name
  const getFieldIcon = useMemo(() => {
    if (!f || !isIconEnabled) return null; // Only show icon if iconEnable feature is active

    const fieldName = f.name?.toLowerCase();
    const fieldType = f.type?.toLowerCase();

    // First check by field name for more specific matches
    if (fieldName && FIELD_ICONS[fieldName]) {
      return FIELD_ICONS[fieldName];
    }

    // Then check by field type
    if (fieldType && FIELD_ICONS[fieldType]) {
      return FIELD_ICONS[fieldType];
    }

    // Default fallback
    return TextIcon;
  }, [f, isIconEnabled]);

  // Set field value from registration data if available
  useEffect(() => {
    if (f?.name === "user_id" && parsedRegistration && parsedRegistration.id) {
      setValue("user_id", parsedRegistration?.id);
    }
    if (f?.name === "Email" && parsedRegistration && parsedRegistration.Email) {
      setValue("Email", parsedRegistration?.Email);
    }
    // Set prefilled email from social auth
    if (
      f?.name?.toLowerCase().includes("email") &&
      prefilledEmail &&
      !rhf.field.value
    ) {
      setValue(f.name, prefilledEmail);
      if (setEmail) {
        setEmail(prefilledEmail);
      }
    }
    // Sponsor pmt link → lock the company field to the sponsor's company (owner 2026-07-17).
    if (isCompanyField && pmt?.company) {
      setValue(f.name, pmt.company);
    }
    if (f?.sx?.display === "none" && !showFamilyMembers) {
      setValue(rhf.field.name, "");
    }
  }, [
    f?.name,
    f?.sx,
    showFamilyMembers,
    parsedRegistration,
    setValue,
    rhf?.field?.name,
    prefilledEmail,
    isCompanyField,
    pmt,
    setEmail,
  ]);

  // Update form value when selectedPermissions changes (only for permission fields)
  useEffect(() => {
    if (f?.type === "permission") {
      const permissionsString = selectedPermissions.join(", ");
      rhf.field.onChange(permissionsString);
    }
  }, [selectedPermissions, rhf.field, f?.type]);

  // Handle email change
  const handleEmailChange = (e) => {
    rhf.field.onChange(e);
    if (setEmail) {
      setEmail(e.target.value);
    }
  };

  // Handle payment change
  const handlePaymentChange = (e) => {
    rhf.field.onChange(e);
    if (setPrice && setOriginalPrice) {
      const value = e.target.value;
      const match = value.match(/\d+(\.\d+)?/);
      if (match) {
        const amount = parseFloat(match[0]);
        setOriginalPrice(amount);
        // Apply discounts. Promo is EITHER percentage (promoDiscount) OR flat (promoFlat);
        // the flat amount is a fixed deduction, clamped so the total never goes negative.
        let finalAmount = amount;
        if (memberDiscount) finalAmount -= memberDiscount;
        if (promoDiscount) finalAmount *= 1 - promoDiscount / 100;
        if (promoFlat) finalAmount = Math.max(0, finalAmount - promoFlat);
        setPrice(finalAmount);
      }
    }
  };

  // Permission field handlers (only used when f.type === "permission")
  const handlePermissionToggle = (permission) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSelectAll = () => {
    if (f?.options) {
      if (selectedPermissions.length === f.options.length) {
        setSelectedPermissions([]);
      } else {
        setSelectedPermissions([...f.options]);
      }
    }
  };

  const handleCustomPermissionsApply = () => {
    if (customPermissions.trim()) {
      const customPerms = customPermissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);

      setSelectedPermissions((prev) => {
        const combined = [...new Set([...prev, ...customPerms])];
        return combined;
      });
      setCustomPermissions("");
      setShowCustomInput(false);
    }
  };

  const handleRemovePermission = (permission) => {
    setSelectedPermissions((prev) => prev.filter((p) => p !== permission));
  };

  // Render field icon
  const renderFieldIcon = () => {
    if (!isIconEnabled || !getFieldIcon) return null;

    const IconComponent = getFieldIcon;
    return (
      <IconContainer>
        <IconComponent size="small" fontSize="small" />
      </IconContainer>
    );
  };

  // Render field label with icon
  const renderFieldLabel = (label) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {isIconEnabled && renderFieldIcon()}
      <Typography variant="body2" fontWeight={600}>
        {label || f.name}
      </Typography>
    </Box>
  );

  // Safety check
  if (!f || !f.type) {
    return null;
  }
  if (f.type === "payment" && !!pmt && pmt?.amount === 0) {
    return null;
  }

  // Smart column calculation for MCQ fields
  const calculateMCQColumns = (field, options) => {
    if (!options || options.length === 0) return { xs: 12, sm: 12, md: 12 };

    // Get field size (default to 12 if not specified)
    const fieldSize = field.md || field.sm || field.xs || 12;

    // Calculate average text length of options
    const avgTextLength =
      options.reduce((sum, option) => sum + option.length, 0) / options.length;

    // Calculate number of options
    const optionCount = options.length;

    // Smart column calculation based on field size and content
    let columns = { xs: 12 }; // Always full width on mobile

    if (fieldSize >= 12) {
      // Full width field - can accommodate more columns
      if (avgTextLength <= 15 && optionCount <= 6) {
        columns.sm = 6; // 2 columns on small screens
        columns.md = 4; // 3 columns on medium screens
        columns.lg = 3; // 4 columns on large screens
      } else if (avgTextLength <= 25 && optionCount <= 4) {
        columns.sm = 6; // 2 columns on small screens
        columns.md = 6; // 2 columns on medium screens
        columns.lg = 4; // 3 columns on large screens
      } else {
        columns.sm = 12; // 1 column on small screens
        columns.md = 6; // 2 columns on medium screens
        columns.lg = 4; // 3 columns on large screens
      }
    } else if (fieldSize >= 8) {
      // 2/3 width field
      if (avgTextLength <= 20 && optionCount <= 4) {
        columns.sm = 6; // 2 columns on small screens
        columns.md = 6; // 2 columns on medium screens
        columns.lg = 4; // 3 columns on large screens
      } else {
        columns.sm = 12; // 1 column on small screens
        columns.md = 12; // 1 column on medium screens
        columns.lg = 6; // 2 columns on large screens
      }
    } else if (fieldSize >= 6) {
      // Half width field
      if (avgTextLength <= 15 && optionCount <= 3) {
        columns.sm = 12; // 1 column on small screens
        columns.md = 12; // 1 column on medium screens
        columns.lg = 6; // 2 columns on large screens
      } else {
        columns.sm = 12; // 1 column on small screens
        columns.md = 12; // 1 column on medium screens
        columns.lg = 12; // 1 column on large screens
      }
    } else {
      // Small field - always single column
      columns.sm = 12;
      columns.md = 12;
      columns.lg = 12;
    }

    return columns;
  };

  // Multiple choice field
  if (f.type === "mcq") {
    const columnSizes = calculateMCQColumns(f, f.options);

    // Find the actual "Other" option value from the options list
    const otherOptionValue =
      f.options?.find(
        (op) => op === "$Other" || op === "Other" || op === "other"
      ) || "Other";

    // Handle mcq change
    const handleMcqChange = (op, checked) => {
      const arr = rhf.field.value || [];
      if (checked) {
        arr.push(op);
      } else if (arr.includes(op)) {
        arr.splice(arr.indexOf(op), 1);
        // If "Other" is unchecked, also remove any "Other: ..." custom values
        if (op === "$Other" || op === "Other" || op === "other") {
          const filteredArr = arr.filter((item) => !item.startsWith("Other:"));
          rhf.field.onChange(filteredArr);
          setOtherValue("");
          return;
        }
      }
      rhf.field.onChange(arr);
    };

    // Handle other input change for mcq - keep "Other" option checked while typing
    const handleMcqOtherInputChange = (e) => {
      const value = e.target.value;
      // Update local state immediately for responsive input (no async delay)
      setLocalOtherValue(value);
      // Update parent state for persistence
      setOtherValue(value);
      // Explicitly keep showOtherInput true while user is typing
      setShowOtherInput(true);

      // Update the selected options array
      const arr = rhf.field.value || [];

      // Remove any existing "Other: ..." custom values but keep all other options
      const filteredArr = arr.filter((item) => !item.startsWith("Other:"));

      // Ensure "Other" option value is in the array (so checkbox stays checked)
      if (!filteredArr.includes(otherOptionValue)) {
        filteredArr.push(otherOptionValue);
      }

      // Add custom value if there's content (preserve spaces in the value)
      if (value && value.length > 0) {
        filteredArr.push(`Other: ${value}`);
      }

      rhf.field.onChange(filteredArr);
    };

    return (
      <AnimatedFormControl>
        <Box>
          <StyledFormControl
            required={f.required}
            sx={showFamilyMembers ? {} : f.sx || {}}
            error={!!rhf.fieldState?.error}
          >
            <FormLabel>{renderFieldLabel(f.label)}</FormLabel>
            <FormGroup>
              <Grid container spacing={1.5}>
                {f.options
                  ?.map((op) => op.trim())
                  ?.map((op, i) => (
                    <Grid
                      item
                      xs={columnSizes.xs}
                      sm={columnSizes.sm}
                      md={columnSizes.md}
                      lg={columnSizes.lg}
                      key={i}
                      sx={{
                        // Ensure mobile responsiveness - FORCE MOBILE LAYOUT
                        ...(isMobile && {
                          flexBasis: "100% !important",
                          maxWidth: "100% !important",
                          width: "100% !important",
                          flex: "0 0 100% !important",
                          minWidth: "100% !important",
                          display: "block !important",
                        }),
                        // Always apply mobile styles regardless of isMobile
                        "@media (max-width: 900px)": {
                          flexBasis: "100% !important",
                          maxWidth: "100% !important",
                          width: "100% !important",
                          flex: "0 0 100% !important",
                          minWidth: "100% !important",
                          display: "block !important",
                        },
                      }}
                    >
                      <Fade
                        in={true}
                        timeout={600}
                        style={{ transitionDelay: `${i * 100}ms` }}
                      >
                        <FormControlLabel
                          control={
                            <StyledCheckbox
                              name={op}
                              checked={
                                op === "$Other" ||
                                op === "Other" ||
                                op === "other"
                                  ? rhf.field.value?.includes(op) ||
                                    rhf.field.value?.some((item) =>
                                      item.startsWith("Other:")
                                    )
                                  : rhf.field.value?.includes(op)
                              }
                              disabled={isUidFieldReadonly}
                              onChange={
                                isUidFieldReadonly
                                  ? () => {}
                                  : (e) => handleMcqChange(op, e.target.checked)
                              }
                            />
                          }
                          label={
                            op === "$Other" ||
                            op === "Other" ||
                            op === "other" ? (
                              "Other"
                            ) : (
                              <div dangerouslySetInnerHTML={{ __html: op }} />
                            )
                          }
                          sx={{
                            mr: 2,
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.95rem",
                            },
                          }}
                        />
                      </Fade>
                    </Grid>
                  ))}
              </Grid>
            </FormGroup>
            {errorMessage && (
              <FormHelperText error>{errorMessage}</FormHelperText>
            )}
          </StyledFormControl>

          {/* Other input field for mcq */}
          <Collapse in={showOtherInput}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 16 }} />
                  You selected "Other" for {f.label || f.name}
                </Typography>
              </Box>
              <StyledTextField
                fullWidth
                label={`Specify ${f.label || f.name}`}
                placeholder={`Enter your ${f.label || f.name.toLowerCase()}`}
                value={localOtherValue}
                onChange={handleMcqOtherInputChange}
                onBlur={rhf.field.onBlur}
                required={f.required}
                error={!!rhf.fieldState?.error}
                helperText={
                  rhf.fieldState?.error
                    ? "Please enter a value"
                    : `Please provide your custom value for ${
                        f.label || f.name
                      }`
                }
                InputProps={{
                  startAdornment: isIconEnabled && (
                    <InputAdornment position="start">
                      {renderFieldIcon()}
                    </InputAdornment>
                  ),
                  endAdornment: !f.readonly &&
                    !isUidFieldReadonly &&
                    isVoiceEnabled && (
                      <InputAdornment position="end">
                        <VoiceButton
                          onVoiceInput={(text) => {
                            setOtherValue(text);
                            handleMcqOtherInputChange({
                              target: { value: text },
                            });
                          }}
                          disabled={f.readonly || isUidFieldReadonly}
                        />
                      </InputAdornment>
                    ),
                }}
              />
            </Box>
          </Collapse>
        </Box>
      </AnimatedFormControl>
    );
  }

  // Radio field
  if (f.type === "radio") {
    const columnSizes = calculateMCQColumns(f, f.options);

    // Find the actual "Other" option value from the options list
    const otherOptionValue =
      f.options?.find(
        (op) => op === "$Other" || op === "Other" || op === "other"
      ) || "Other";

    // Handle radio change
    const handleRadioChange = (e) => {
      const selectedValue = e.target.value;

      // If "Other" is selected, show the input field
      if (
        selectedValue === "$Other" ||
        selectedValue === "Other" ||
        selectedValue === "other"
      ) {
        setShowOtherInput(true);
        // Keep the form value as the "Other" option value, custom text is stored separately
        rhf.field.onChange(otherOptionValue);
        // If we have a custom value, update it to the form field (preserve spaces)
        if (otherValue) {
          rhf.field.onChange(otherValue);
        }
      } else {
        // Regular option selected
        setShowOtherInput(false);
        setOtherValue("");
        rhf.field.onChange(selectedValue);
      }
    };

    // Handle other input change for radio - store in state and update form value
    const handleRadioOtherInputChange = (e) => {
      const value = e.target.value;
      // Update local state immediately for responsive input (no async delay)
      setLocalOtherValue(value);
      // Update parent state for persistence
      setOtherValue(value);
      // Explicitly keep showOtherInput true while user is typing
      setShowOtherInput(true);
      // Update form field with custom value (preserve spaces exactly as typed)
      // Use the value directly without any processing to preserve all spaces
      rhf.field.onChange(value || otherOptionValue);
    };

    // Handle blur to ensure form value is synced
    const handleRadioOtherInputBlur = (e) => {
      rhf.field.onBlur(e);
      // Ensure form value is updated with the final value (preserving spaces)
      const finalValue = localOtherValue || otherOptionValue;
      rhf.field.onChange(finalValue);
      // Sync parent state with final value
      setOtherValue(finalValue);
    };

    return (
      <AnimatedFormControl>
        <Box>
          <StyledFormControl
            required={f.required}
            sx={showFamilyMembers ? {} : f.sx || {}}
            error={!!rhf.fieldState?.error}
          >
            <FormLabel>{renderFieldLabel(f.label)}</FormLabel>
            <RadioGroup
              name={f.name}
              onChange={isUidFieldReadonly ? () => {} : handleRadioChange}
              onBlur={rhf.field.onBlur}
              value={
                showOtherInput
                  ? otherOptionValue
                  : f.options?.includes(rhf.field.value)
                  ? rhf.field.value
                  : ""
              }
            >
              <Grid container spacing={1.5}>
                {f.options?.map((op, i) => (
                  <Grid
                    item
                    xs={columnSizes.xs}
                    sm={columnSizes.sm}
                    md={columnSizes.md}
                    lg={columnSizes.lg}
                    key={i}
                    sx={{
                      // Ensure mobile responsiveness
                      ...(isMobile && {
                        flexBasis: "100% !important",
                        maxWidth: "100% !important",
                        width: "100% !important",
                      }),
                      // Always apply mobile styles regardless of isMobile
                      "@media (max-width: 900px)": {
                        flexBasis: "100% !important",
                        maxWidth: "100% !important",
                        width: "100% !important",
                        flex: "0 0 100% !important",
                        minWidth: "100% !important",
                        display: "block !important",
                      },
                    }}
                  >
                    <Fade
                      in={true}
                      timeout={600}
                      style={{ transitionDelay: `${i * 100}ms` }}
                    >
                      <FormControlLabel
                        control={<StyledRadio disabled={isUidFieldReadonly} />}
                        label={
                          op === "$Other" || op === "Other" || op === "other"
                            ? "Other"
                            : op
                        }
                        value={op}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.95rem",
                          },
                        }}
                      />
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
            {errorMessage && (
              <FormHelperText error>{errorMessage}</FormHelperText>
            )}
          </StyledFormControl>

          {/* Other input field for radio */}
          <Collapse in={showOtherInput}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 16 }} />
                  You selected "Other" for {f.label || f.name}
                </Typography>
              </Box>
              <StyledTextField
                fullWidth
                label={`Specify ${f.label || f.name}`}
                placeholder={`Enter your ${f.label || f.name.toLowerCase()}`}
                value={localOtherValue}
                onChange={handleRadioOtherInputChange}
                onBlur={handleRadioOtherInputBlur}
                required={f.required}
                error={!!rhf.fieldState?.error}
                helperText={
                  rhf.fieldState?.error
                    ? "Please enter a value"
                    : `Please provide your custom value for ${
                        f.label || f.name
                      }`
                }
                InputProps={{
                  startAdornment: isIconEnabled && (
                    <InputAdornment position="start">
                      {renderFieldIcon()}
                    </InputAdornment>
                  ),
                  endAdornment: !f.readonly &&
                    !isUidFieldReadonly &&
                    isVoiceEnabled && (
                      <InputAdornment position="end">
                        <VoiceButton
                          onVoiceInput={(text) => {
                            setOtherValue(text);
                            handleRadioOtherInputChange({
                              target: { value: text },
                            });
                          }}
                          disabled={f.readonly || isUidFieldReadonly}
                        />
                      </InputAdornment>
                    ),
                }}
              />
            </Box>
          </Collapse>
        </Box>
      </AnimatedFormControl>
    );
  }

  // Payment field
  if (f.type === "payment") {
    return (
      <AnimatedFormControl>
        <StyledFormControl
          required={f.required}
          sx={showFamilyMembers ? {} : f.sx || {}}
          error={!!rhf.fieldState?.error}
        >
          <FormLabel>{renderFieldLabel(f.label)}</FormLabel>
          <RadioGroup
            name={f.name}
            onChange={isUidFieldReadonly ? () => {} : handlePaymentChange}
            onBlur={rhf.field.onBlur}
            value={rhf.field.value}
          >
            <Grid container spacing={1.5}>
              {f.options?.map((op, i) => (
                <Grid
                  item
                  xs={12} // Always full width on mobile
                  sm={f.sm || f.xs || 12}
                  md={f.md || f.xs || 12}
                  lg={f.md || f.xs || 12}
                  key={i}
                  sx={{
                    // Ensure mobile responsiveness
                    ...(isMobile && {
                      flexBasis: "100% !important",
                      maxWidth: "100% !important",
                      width: "100% !important",
                    }),
                  }}
                >
                  <Fade
                    in={true}
                    timeout={600}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <FormControlLabel
                      control={<StyledRadio disabled={isUidFieldReadonly} />}
                      label={op === "$Other" ? "Other:" : op}
                      value={op}
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.95rem",
                        },
                      }}
                    />
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
        </StyledFormControl>
      </AnimatedFormControl>
    );
  }

  // HTML field
  if (f.type === "html") {
    return (
      <AnimatedFormControl>
        <Box
          sx={{
            py: 2,
            px: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: f.defaultValue }} />
        </Box>
      </AnimatedFormControl>
    );
  }

  // Checkbox field
  if (f.type === "checkbox") {
    return (
      <AnimatedFormControl>
        <StyledFormControl
          required={f.required}
          fullWidth
          sx={showFamilyMembers ? {} : f.sx || {}}
          error={!!rhf.fieldState?.error}
        >
          <FormControlLabel
            onChange={isUidFieldReadonly ? () => {} : rhf.field.onChange}
            onBlur={rhf.field.onBlur}
            inputRef={rhf.field.ref}
            control={<StyledCheckbox disabled={isUidFieldReadonly} />}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {renderFieldIcon()}
                <Typography variant="body2">{f.label || f.name}</Typography>
              </Box>
            }
            name={rhf.field.name}
            checked={rhf.field.value}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.95rem",
              },
            }}
          />
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
        </StyledFormControl>
      </AnimatedFormControl>
    );
  }

  // Select field
  if (f.type === "select") {
    // Find the actual "Other" option value from the options list
    const otherOptionValue =
      f.options?.find(
        (op) => op === "$Other" || op === "Other" || op === "other"
      ) || "Other";

    // Handle select change
    const handleSelectChange = (e) => {
      const selectedValue = e.target.value;

      // If "Other" is selected, show the input field
      if (
        selectedValue === "$Other" ||
        selectedValue === "Other" ||
        selectedValue === "other"
      ) {
        setShowOtherInput(true);
        // Keep the form value as the "Other" option value, custom text is stored separately
        rhf.field.onChange(otherOptionValue);
        // If we have a custom value, update it to the form field (preserve spaces)
        if (otherValue) {
          rhf.field.onChange(otherValue);
        }
      } else {
        // Regular option selected
        setShowOtherInput(false);
        setOtherValue("");
        rhf.field.onChange(selectedValue);
      }
    };

    // Handle other input change - store in state and update form value
    const handleOtherInputChange = (e) => {
      const value = e.target.value;
      // Update local state immediately for responsive input (no async delay)
      setLocalOtherValue(value);
      // Update parent state for persistence
      setOtherValue(value);
      // Explicitly keep showOtherInput true while user is typing
      setShowOtherInput(true);
      // Update form field with custom value (preserve spaces exactly as typed)
      // Use the value directly without any processing to preserve all spaces
      rhf.field.onChange(value || otherOptionValue);
    };

    // Handle blur to ensure form value is synced
    const handleOtherInputBlur = (e) => {
      rhf.field.onBlur(e);
      // Ensure form value is updated with the final value (preserving spaces)
      const finalValue = localOtherValue || otherOptionValue;
      rhf.field.onChange(finalValue);
      // Sync parent state with final value
      setOtherValue(finalValue);
    };

    // Handler to go back to select dropdown
    const handleBackToSelect = () => {
      setShowOtherInput(false);
      setLocalOtherValue("");
      setOtherValue("");
      rhf.field.onChange(otherOptionValue);
    };

    return (
      <AnimatedFormControl>
        <Box>
          {/* Show select dropdown only when "Other" is not selected */}
          {!showOtherInput ? (
            <StyledTextField
              name={rhf.field.name}
              value={
                f.options?.includes(rhf.field.value) ? rhf.field.value : ""
              }
              onChange={isUidFieldReadonly ? () => {} : handleSelectChange}
              onBlur={rhf.field.onBlur}
              inputRef={rhf.field.ref}
              error={!!rhf.fieldState?.error}
              helperText={errorMessage}
              label={f.label || f.name}
              required={f.required}
              sx={showFamilyMembers ? {} : f.sx || {}}
              fullWidth
              select
              variant="outlined"
              disabled={isUidFieldReadonly}
              InputProps={{
                startAdornment: isIconEnabled && (
                  <InputAdornment position="start">
                    {renderFieldIcon()}
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">None</MenuItem>
              {f.options?.map((op) => (
                <MenuItem key={op} value={op}>
                  {op === "$Other" || op === "Other" || op === "other"
                    ? "Other"
                    : op}
                </MenuItem>
              ))}
            </StyledTextField>
          ) : (
            /* Show "Other" input field when "Other" is selected */
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  {f.label || f.name} - Other
                </Typography>
                <Button
                  size="small"
                  onClick={handleBackToSelect}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  Change
                </Button>
              </Box>
              <StyledTextField
                fullWidth
                label={`Specify ${f.label || f.name}`}
                placeholder={`Enter your ${f.label || f.name.toLowerCase()}`}
                value={localOtherValue}
                onChange={handleOtherInputChange}
                onBlur={handleOtherInputBlur}
                required={f.required}
                error={!!rhf.fieldState?.error}
                helperText={
                  rhf.fieldState?.error
                    ? "Please enter a value"
                    : `You selected "Other" for ${
                        f.label || f.name
                      }. Please specify your value.`
                }
                InputProps={{
                  startAdornment: isIconEnabled && (
                    <InputAdornment position="start">
                      {renderFieldIcon()}
                    </InputAdornment>
                  ),
                  endAdornment: !f.readonly &&
                    !isUidFieldReadonly &&
                    isVoiceEnabled && (
                      <InputAdornment position="end">
                        <VoiceButton
                          onVoiceInput={(text) => {
                            setLocalOtherValue(text);
                            setOtherValue(text);
                            rhf.field.onChange(text);
                          }}
                          disabled={f.readonly || isUidFieldReadonly}
                        />
                      </InputAdornment>
                    ),
                }}
              />
            </Box>
          )}
        </Box>
      </AnimatedFormControl>
    );
  }

  // Country field
  if (f.type === "country") {
    return (
      <AnimatedFormControl>
        <Autocomplete
          options={countries}
          autoHighlight
          value={
            !rhf.field.value
              ? countries.find((c) => c.code === "OM")
              : countries.find((c) => c.label === rhf.field.value) ||
                countries.find((c) => c.code === rhf.field.value)
          }
          onChange={(e, v) => {
            rhf.field.onChange(v?.label || ""); // Save full country name instead of code
            setValue("calling_code", v?.dial_code || "");
            setSelectedCountry(v?.label || "");
          }}
          onBlur={rhf.field.onBlur}
          getOptionLabel={(o) => o?.label || ""}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              <img
                loading="lazy"
                width="20"
                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                alt=""
              />
              {option.label} ({option.code}) {option.dial_code}
            </Box>
          )}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              inputProps={{
                ...params.inputProps,
                autoComplete: "new-password",
              }}
              name={rhf.field.name}
              inputRef={rhf.field.ref}
              error={!!rhf.fieldState?.error}
              helperText={errorMessage}
              label={f.label || f.name}
              placeholder={f.placeholder}
              required={f.required}
              sx={showFamilyMembers ? {} : f.sx || {}}
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isIconEnabled && renderFieldIcon()}
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${(
                          countries.find((c) => c.label === rhf.field.value)
                            ?.code ||
                          countries.find((c) => c.code === rhf.field.value)
                            ?.code ||
                          "om"
                        ).toLowerCase()}.png`}
                        alt="country_flag"
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </AnimatedFormControl>
    );
  }

  // Mobile code field
  if (f.type === "mobile_code" || f.type === "calling_code") {
    return (
      <AnimatedFormControl>
        <Autocomplete
          options={countries}
          autoHighlight
          value={
            !rhf.field.value
              ? countries.find((c) => c.code === "OM")
              : countries.find((c) => c.dial_code === rhf.field.value)
          }
          onChange={(e, v) => {
            rhf.field.onChange(v?.dial_code || "");
            setSelectedMobileCode(v?.dial_code || "");
            if (findCountryCodeByDialCode) {
              setSelectedCountry(findCountryCodeByDialCode(v?.dial_code));
            }
          }}
          onBlur={rhf.field.onBlur}
          getOptionLabel={(o) => `${o?.dial_code || ""} (${o?.label || ""})`}
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              <img
                loading="lazy"
                width="20"
                srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                alt=""
              />
              {option.dial_code} ({option.label})
            </Box>
          )}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              inputProps={{
                ...params.inputProps,
                autoComplete: "new-password",
              }}
              name={rhf.field.name}
              inputRef={rhf.field.ref}
              error={!!rhf.fieldState?.error}
              helperText={errorMessage}
              label={f.label || f.name}
              placeholder={f.placeholder}
              required={f.required}
              sx={showFamilyMembers ? {} : f.sx || {}}
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {/* {isIconEnabled && renderFieldIcon()} */}
                      <img
                        loading="lazy"
                        width="20"
                        src={`https://flagcdn.com/w20/${
                          countries
                            .find((c) => c.dial_code === rhf.field.value)
                            ?.code?.toLowerCase() || "om"
                        }.png`}
                        alt="country_flag"
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </AnimatedFormControl>
    );
  }

  // Email field
  if (f.type === "email") {
    // Check if this is a social auth email field
    const isSocialAuthEmail =
      prefilledEmail &&
      socialProvider &&
      f.name.toLowerCase().includes("email");

    return (
      <AnimatedFormControl>
        <StyledTextField
          type="email"
          InputProps={{
            autoComplete:
              f.autoComplete || f.type === "confirm_email"
                ? "new-password"
                : "on",
            readOnly: f.readonly || isSocialAuthEmail || isUidFieldReadonly,
            startAdornment: isIconEnabled && (
              <InputAdornment position="start">
                {renderFieldIcon()}
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {/* Social auth indicator */}
                {isSocialAuthEmail && (
                  <InputAdornment position="end">
                    <Tooltip title={`Email verified via ${socialProvider}`}>
                      <IconButton size="small" disabled>
                        <VerifiedIcon
                          sx={{ color: "success.main", fontSize: 18 }}
                        />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )}
                {/* Voice input */}
                {!f.readonly &&
                  !isSocialAuthEmail &&
                  !isUidFieldReadonly &&
                  isVoiceEnabled && (
                    <InputAdornment position="end">
                      <VoiceButton
                        onVoiceInput={(text) => {
                          rhf.field.onChange(text);
                          handleEmailChange({ target: { value: text } });
                        }}
                        disabled={
                          f.readonly || isSocialAuthEmail || isUidFieldReadonly
                        }
                      />
                    </InputAdornment>
                  )}
              </>
            ),
          }}
          name={rhf.field.name}
          value={rhf.field.value}
          onChange={isSocialAuthEmail ? () => {} : handleEmailChange}
          onBlur={rhf.field.onBlur}
          inputRef={rhf.field.ref}
          error={
            !!rhf.fieldState?.error ||
            (typeof isEmailTaken === "string" &&
              isEmailTaken.toLowerCase().includes("already registered"))
          }
          helperText={
            isSocialAuthEmail
              ? `Email verified via ${socialProvider}`
              : emailCheckLoading
              ? "Checking email..."
              : // Duplicate is surfaced via the bottom toast (owner: don't repeat it as a red line under
                // the field). Keep only the red error state on the field.
                errorMessage
          }
          label={f.label || f.name}
          placeholder={f.placeholder}
          required={f.required}
          sx={{
            ...(!showFamilyMembers ? f.sx || {} : {}),
            ...(isSocialAuthEmail && {
              "& .MuiInputBase-input": {
                backgroundColor: alpha(
                  parsedTheme?.primary || theme.palette.primary.main,
                  0.05
                ),
                color: parsedTheme?.primary || theme.palette.primary.main,
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: alpha(
                    parsedTheme?.primary || theme.palette.primary.main,
                    0.3
                  ),
                },
                "&:hover fieldset": {
                  borderColor: alpha(
                    parsedTheme?.primary || theme.palette.primary.main,
                    0.5
                  ),
                },
                "&.Mui-focused fieldset": {
                  borderColor:
                    parsedTheme?.primary || theme.palette.primary.main,
                },
              },
            }),
          }}
          fullWidth
        />
      </AnimatedFormControl>
    );
  }

  // Permission field
  if (f.type === "permission") {
    const isAllSelected = selectedPermissions.length === f.options.length;

    return (
      <AnimatedFormControl>
        <StyledFormControl
          required={f.required}
          sx={{ ...(!showFamilyMembers ? f.sx || {} : {}), width: "100%" }}
          error={!!rhf.fieldState?.error}
        >
          <FormLabel sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isIconEnabled && renderFieldIcon()}
              <Typography variant="body2" fontWeight={600}>
                {f.label || f.name || "Permissions"}
              </Typography>
            </Box>
          </FormLabel>

          {/* Control buttons */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectAll}
              startIcon={
                isAllSelected ? (
                  <RadioButtonUncheckedIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                borderColor: parsedTheme?.primary || theme.palette.primary.main,
                color: parsedTheme?.primary || theme.palette.primary.main,
                "&:hover": {
                  borderColor:
                    parsedTheme?.secondary || theme.palette.primary.dark,
                  backgroundColor: alpha(
                    parsedTheme?.primary || theme.palette.primary.main,
                    0.1
                  ),
                },
              }}
            >
              {isAllSelected ? "Deselect All" : "Select All"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowCustomInput(!showCustomInput)}
              startIcon={
                showCustomInput ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{
                borderColor:
                  parsedTheme?.secondary || theme.palette.secondary.main,
                color: parsedTheme?.secondary || theme.palette.secondary.main,
                "&:hover": {
                  borderColor:
                    parsedTheme?.primary || theme.palette.secondary.dark,
                  backgroundColor: alpha(
                    parsedTheme?.secondary || theme.palette.secondary.main,
                    0.1
                  ),
                },
              }}
            >
              Custom Permissions
            </Button>
          </Stack>

          {/* Selected permissions chips */}
          {selectedPermissions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Selected Permissions ({selectedPermissions.length}):
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 1 }}
              >
                {selectedPermissions.map((permission, index) => (
                  <Chip
                    key={index}
                    label={permission}
                    size="small"
                    onDelete={() => handleRemovePermission(permission)}
                    sx={{
                      backgroundColor: alpha(
                        parsedTheme?.primary || theme.palette.primary.main,
                        0.1
                      ),
                      color: parsedTheme?.primary || theme.palette.primary.main,
                      "& .MuiChip-deleteIcon": {
                        color:
                          parsedTheme?.primary || theme.palette.primary.main,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Custom permissions input */}
          <Collapse in={showCustomInput}>
            <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Enter custom permissions (comma-separated):
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  value={customPermissions}
                  onChange={(e) => setCustomPermissions(e.target.value)}
                  placeholder="e.g., custom-perm1, custom-perm2"
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCustomPermissionsApply}
                  disabled={!customPermissions.trim()}
                  sx={{
                    backgroundColor:
                      parsedTheme?.primary || theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor:
                        parsedTheme?.secondary || theme.palette.primary.dark,
                    },
                  }}
                >
                  Add
                </Button>
              </Stack>
            </Box>
          </Collapse>

          {/* Permission options grid */}
          <FormGroup>
            <Grid container spacing={1}>
              {f.options?.map((permission, i) => (
                <Grid
                  item
                  xs={12} // Always full width on mobile
                  sm={6}
                  md={4}
                  key={i}
                  sx={{
                    // Ensure mobile responsiveness
                    ...(isMobile && {
                      flexBasis: "100% !important",
                      maxWidth: "100% !important",
                      width: "100% !important",
                    }),
                  }}
                >
                  <Fade
                    in={true}
                    timeout={600}
                    style={{ transitionDelay: `${i * 50}ms` }}
                  >
                    <FormControlLabel
                      control={
                        <StyledCheckbox
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => handlePermissionToggle(permission)}
                          name={permission}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {permission}
                        </Typography>
                      }
                      sx={{
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem",
                          wordBreak: "break-word",
                        },
                        "&:hover": {
                          backgroundColor: alpha(
                            parsedTheme?.primary || theme.palette.primary.main,
                            0.05
                          ),
                        },
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    />
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </FormGroup>

          {/* Hidden input for form validation */}
          <TextField
            sx={{ display: "none" }}
            name={rhf.field.name}
            value={rhf.field.value || ""}
            inputRef={rhf.field.ref}
            error={!!rhf.fieldState?.error}
          />

          {errorMessage && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </StyledFormControl>
      </AnimatedFormControl>
    );
  }

  // Default field (text, number, etc.)
  // Check if this is a mobile field that needs duplicate checking
  const isMobileField =
    f.name.toLowerCase().includes("mobile") ||
    f.name.toLowerCase().includes("phone") ||
    f.type === "mobile";

  const hasMobileDuplicateError =
    isMobileField && isMobileTaken === "Mobile number already exists";

  const mobileHelperText = isMobileField
    ? mobileCheckLoading
      ? "Checking mobile..."
      : hasMobileDuplicateError
      ? "This mobile number is already registered"
      : errorMessage
    : errorMessage;

  // Owner 2026-08-05: a sponsor-locked company field must READ as locked — non-editable, non-clickable,
  // with a hover tooltip — so an exhibitor doesn't try to change it and then complain it "won't change".
  // (The value is server-enforced anyway on a pmt link.) Only applies when the company is pmt-locked.
  const companyLockActive = isPmtCompanyReadonly;
  const companyLockTip =
    "Company is set by your sponsor and can't be changed here.";

  const lockedTextField = (
    <AnimatedFormControl>
      <StyledTextField
        // CEO #3: mobile/phone fields accept digits only (allow a leading '+').
        // Use type="tel" + inputMode="numeric" (no number-spinner / locale issues)
        // and strip non-digits on change/paste.
        type={isMobileField ? "tel" : f.type === "number" ? "number" : "text"}
        inputProps={
          isMobileField
            ? { inputMode: "numeric", pattern: "[0-9+]*", maxLength: 20 }
            : undefined
        }
        InputProps={{
          autoComplete:
            f.autoComplete || f.type === "confirm_email"
              ? "new-password"
              : "on",
          readOnly: f.readonly || isUidFieldReadonly || isPmtCompanyReadonly,
          startAdornment: isIconEnabled && (
            <InputAdornment position="start">
              {renderFieldIcon()}
            </InputAdornment>
          ),
          endAdornment: !f.readonly &&
            !isUidFieldReadonly &&
            f.type !== "number" &&
            isVoiceEnabled && (
              <InputAdornment position="end">
                <VoiceButton
                  onVoiceInput={(text) => {
                    rhf.field.onChange(text);
                  }}
                  disabled={f.readonly || isUidFieldReadonly}
                />
              </InputAdornment>
            ),
        }}
        name={rhf.field.name}
        value={rhf.field.value}
        onChange={
          isMobileField
            ? (e) => {
                // Keep only digits; allow a single leading '+' for country code.
                const raw = e.target.value;
                const hasPlus = raw.trim().startsWith("+");
                const digits = raw.replace(/[^0-9]/g, "");
                rhf.field.onChange((hasPlus ? "+" : "") + digits);
              }
            : rhf.field.onChange
        }
        onBlur={rhf.field.onBlur}
        inputRef={rhf.field.ref}
        error={!!rhf.fieldState?.error || hasMobileDuplicateError}
        helperText={companyLockActive ? companyLockTip : mobileHelperText}
        label={f.label || f.name}
        placeholder={f.placeholder}
        required={f.required}
        sx={{
          ...(showFamilyMembers ? {} : f.sx || {}),
          // Locked company: read as disabled — muted, not-allowed cursor, no text caret/selection.
          ...(companyLockActive && {
            "& .MuiInputBase-root": {
              backgroundColor: "action.hover",
              cursor: "not-allowed",
            },
            "& .MuiInputBase-input": {
              cursor: "not-allowed",
              // block the caret + text selection so it never looks editable
              caretColor: "transparent",
              userSelect: "none",
            },
          }),
        }}
        fullWidth
      />
    </AnimatedFormControl>
  );

  // Wrap the locked company field in a tooltip so hovering explains WHY it's fixed.
  return companyLockActive ? (
    <Tooltip title={companyLockTip} arrow placement="top">
      {/* span wrapper: MUI Tooltip needs a DOM node that forwards a ref for a disabled/readonly child */}
      <span style={{ display: "block", cursor: "not-allowed" }}>{lockedTextField}</span>
    </Tooltip>
  ) : (
    lockedTextField
  );
};

export default FormField;
